// @ts-nocheck

import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from '@features/otp-verification/styles/PinFormV5.module.css';
import lgStyles from '@features/otp-verification/styles/LgAg26PinForm.module.css';
import { isBH, isID, isKW, isOM } from '@entities/country/model/utils';
import { isMoroccoDvContext } from '@entities/operator';
import { isRTL } from '@shared/utils/general';
import Timer from '@shared/ui/timer/ui/Timer';
import { PinFormOperatorText } from '@features/otp-verification/ui/PinFormOperatorText';
import { InputErrorMessage } from '@shared/ui/input-error-message/ui/InputErrorMessage';
import { dismissAlert } from '@shared/store/slices/alert.actions';
import { BannerTitleV5 } from '@shared/ui/banner-title-v5/ui/BannerTitleV5';
import { LottieAnimation } from '@shared/ui/lottie-animation';
import {
  UnlockVuzBanner,
  StAg26StepsBanner,
  LgAg26Footer,
  isStAg26Variation,
  isAg26MarketingVariation,
} from '@shared/ui/unlock-vuz-banner';
import { useOTPLogic } from '@features/otp-verification/model/useOTPLogic';
import { useCheckMarkLYCondition } from '@features/phone-subscription/model/hooks/useCheckMarkLYCondition';
import { CheckMarkLYFooter } from '@features/phone-subscription/ui/CheckMarkLYFooter';
import cn from 'classnames';

export const PinFormV5 = () => {
  const { t } = useTranslation();

  const {
    pin,
    invalidLength,
    errors,
    inputId,
    language,
    homeData,
    subscribeData,
    operator,
    source,
    btnLoading,
    alerts,
    otpDigitsCount,
    minPinLength,
    maxPinLength,
    handlePinChange,
    handleSubmit,
  } = useOTPLogic({ variant: 'v5' });

  const { isCheckMarkLY } = useCheckMarkLYCondition();
  const isStAg26 = isStAg26Variation();
  const isAg26 = isAg26MarketingVariation();

  const isMoroccoDv = isMoroccoDvContext(
    source,
    operator?.value,
    homeData?.source,
    subscribeData?.operatorName,
    homeData?.operators?.[0]?.value
  );
  const inputPlaceholder = Array(otpDigitsCount).fill('•').join(' ');
  const isOTPError = alerts.length && (alerts[0].msg?.message || alerts[0].msg);
  const validationError = errors.pin ? t(isMoroccoDv ? 'dv_invalid_pin' : 'invalid_pin') : null;
  const displayedError = isOTPError ? alerts[0].msg?.message || alerts[0].msg : validationError;
  const dismissDisplayedError =
    isOTPError && alerts[0]?.dismissible && alerts[0]?.id
      ? () => dismissAlert(alerts[0].id)
      : undefined;
  const isKuwait = isKW(homeData?.country);

  const isButtonDisabled =
    !pin ||
    pin.length < minPinLength ||
    pin.length > maxPinLength ||
    btnLoading ||
    invalidLength;

  if (isAg26) {
    return (
      <div className={lgStyles.page}>
        <form className={lgStyles.wrapper} onSubmit={(e) => handleSubmit(e)} id="otp-pin-form">
          <div className={lgStyles.bannerContainer}>
            {isStAg26 ? (
              <StAg26StepsBanner showTrial={false} />
            ) : (
              <UnlockVuzBanner showTrial={false} />
            )}
          </div>

          <div className={lgStyles.card}>
            <p className={lgStyles.title}>{t('lg_ag26_otp_title')}</p>

            <div className={lgStyles.inputGroup}>
              <p className={lgStyles.label}>{t('lg_ag26_otp_label')}</p>

              <div className={lgStyles.field}>
                <input
                  className={cn(lgStyles.input, displayedError ? lgStyles.inputError : '')}
                  id={inputId}
                  type="tel"
                  minLength={minPinLength}
                  maxLength={maxPinLength}
                  onChange={(e) => handlePinChange(e)}
                  name="pin"
                  autoComplete="one-time-code"
                  value={pin}
                  placeholder={t('lg_ag26_otp_placeholder')}
                />

                <InputErrorMessage
                  className={lgStyles.errorMessage}
                  error={displayedError}
                  onDismiss={dismissDisplayedError}
                />
              </div>

              <button
                disabled={isButtonDisabled}
                type="submit"
                id="submit-otp"
                className={lgStyles.confirmButton}
              >
                {t('lg_ag26_otp_confirm')}
              </button>
            </div>

            <div className={cn(lgStyles.timer, 'timer_container')}>
              <Timer pin={pin} />
            </div>
          </div>

          <div className={lgStyles.disclaimer}>
            <div dangerouslySetInnerHTML={{ __html: homeData.fullTCText }} />
          </div>
        </form>

        <LgAg26Footer />
      </div>
    );
  }

  return (
    <form
      className={cn(styles.wrapper, 'container-sm')}
      onSubmit={(e) => handleSubmit(e)}
      id="otp-pin-form"
    >
      <div className={styles.lottieAnimationContainer}>
        <LottieAnimation />
      </div>

      <BannerTitleV5 subTitle={t('enter_otp_from_sms')} />

      <div className={styles.otpInputContainer}>
        <input
          className={cn(styles.otpInput, displayedError ? styles.otpInputError : '')}
          id={inputId}
          type="tel"
          minLength={minPinLength}
          maxLength={maxPinLength}
          onChange={(e) => handlePinChange(e)}
          name="pin"
          autoComplete="new-password"
          value={pin}
          placeholder={inputPlaceholder}
        />

        <InputErrorMessage
          className={styles.otpError}
          error={displayedError}
          onDismiss={dismissDisplayedError}
        />
      </div>

      <div className={cn(styles.timerContainer, 'timer_container')}>
        <Timer pin={pin} />
      </div>

      <div className={cn(styles.otpButtonContainer, isCheckMarkLY && styles.otpButtonContainerLy)}>
        <button
          disabled={isButtonDisabled}
          type="submit"
          id="submit-otp"
          className="dcb_subscribe_button_v5"
        >
          {isCheckMarkLY
            ? t('check_mark_ly_confirm_subscription')
            : isOM(homeData?.country) ||
              isKW(homeData?.country) ||
              isBH(homeData?.country) ||
              isID(homeData?.country)
            ? t('lottie_button_subscribe')
            : t('continue')}
        </button>

        {!isKuwait && !isCheckMarkLY && (
          <div className={styles.pinOperatorText}>
            <PinFormOperatorText />
          </div>
        )}
      </div>

      <div
        className={cn(isRTL(language.direction) ? styles.tcContainerRTL : styles.tcContainer, {
          [styles.kw_gap]: isKuwait,
          cptpl_terms: isCheckMarkLY,
        })}
        dangerouslySetInnerHTML={{ __html: homeData.fullTCText }}
      />

      {isCheckMarkLY ? <CheckMarkLYFooter /> : null}
    </form>
  );
};
